import { useRef, useState } from 'react';
import { Button, Dialog } from '@citibox-panel/ui';
import { track } from '@citibox-panel/analytics';
import type { CarrierOption, LocationMatch } from '@citibox-panel/api-client';
import { type LocationFilters } from '../../search-shell/model/shellState';
import { DEFAULT_DELIVERY_RANGE } from '../../../lib/deliveryRange';
import { countBucket } from '../../../lib/countBucket';
import { CoincidencesHeader } from './CoincidencesHeader';
import { CoincidencesStep } from './CoincidencesStep';
import { FiltersHeader } from './FiltersHeader';
import { FiltersStep } from './FiltersStep';

// Module scope, so the pristine set has one stable identity across sessions — no step mutates it.
// The range is the only thing a pristine draft carries: no carrier keys means every carrier, and
// the box is optional, so this set is already a complete, searchable filter.
const PRISTINE: LocationFilters = { days: DEFAULT_DELIVERY_RANGE };

export interface LocationSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onExited: () => void;
  // Reports what was confirmed and nothing else. A caller that acts on it outside the dialog waits
  // for `onExited` — this component holds no opinion about the page it closes over.
  onConfirm: (location: LocationMatch, filters: LocationFilters) => void;
  searchLocations: (q: string) => Promise<LocationMatch[]>;
  fetchLocationCarriers: (locationId: number) => Promise<CarrierOption[]>;
  fetchDeliveryStates: () => Promise<string[]>;
  initialQuery?: string;
  initialLocation?: LocationMatch;
  initialFilters?: LocationFilters;
}

export function LocationSearchDialog({
  open,
  onClose,
  onExited,
  onConfirm,
  initialQuery,
  initialLocation,
  initialFilters,
  searchLocations,
  fetchLocationCarriers,
  fetchDeliveryStates,
}: LocationSearchDialogProps) {
  // The initialisers only ever cover a caller that mounts already-open; the transition below seeds
  // every session after that.
  const [openedFor, setOpenedFor] = useState(open);
  // Deliberately not folded into `openedFor`: that boolean answers «did `open` change», this
  // counter answers «which session is this», and a boolean cannot tell the third open from the
  // first.
  const [session, setSession] = useState(0);
  const [location, setLocation] = useState(initialLocation);
  const [query, setQuery] = useState(initialQuery ?? '');
  const [filters, setFilters] = useState(initialFilters ?? PRISTINE);

  // Seeded during RENDER, never in an effect. This component outlives its sessions — `open` is a
  // prop — so a session's state has to be established somewhere, and here React discards the render
  // that sets it and retries BEFORE rendering children: the last session's drafts are a value no
  // step can observe, whatever order effects happen to run in. An effect only ever seeds a step
  // that has already mounted and, over a warm search cache, already resolved and reported.
  if (open !== openedFor) {
    // Updated inside the branch, or the render loops.
    setOpenedFor(open);
    if (open) {
      // On OPEN only: the children stay mounted through the exit transition, so reseeding on close
      // would rewrite the dialog as it fades out.
      setSession((n) => n + 1);
      setLocation(initialLocation);
      setQuery(initialQuery ?? '');
      setFilters(initialFilters ?? PRISTINE);
    }
  }

  // The event is owned here for its LIFETIME, not for its data: a search session in the modal is
  // what it counts, and only this component lives that long — the step that has the numbers dies on
  // every «Cambiar», so a guard there would be empty exactly when the agent comes back to a term
  // already counted.
  //
  // SCOPED by session rather than cleared when one starts: a key cannot be applied at the wrong
  // moment, whereas a reset can. Single term rather than a set, because the rule is «report when the
  // resolved term changes» — retyping an earlier term within a session is a new search.
  const reported = useRef<{ session: number; term: string } | null>(null);

  // Takes the resolved term to dedupe on and a COUNT, so no address is ever in reach of a payload.
  const handleResolved = (term: string, matches: number): void => {
    // Hoisted: comparing through an optional chain narrows nothing, so the second conjunct of
    // `reported.current?.session === session && reported.current.term === term` would not compile.
    const seen = reported.current;
    if (seen !== null && seen.session === session && seen.term === term) return;
    reported.current = { session, term };
    track('location_search_submitted', { matches_bucket: countBucket(matches) });
  };

  // What «Buscar» DOES, in one place: it has two triggers — the footer's button and Enter in the
  // filters step's box field — and a copy per trigger is a confirm that can drift from the other.
  // The guard is for `tsc` alone: both triggers only exist on step 2, which is derived from a
  // selected location.
  const handleSearch = (): void => {
    if (location === undefined) return;
    onConfirm(location, filters);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Forwarded, not interpreted: what «the page is addressable again» is worth belongs to the
      // caller, and MUI's modality is a fact about the modal rather than about this feature.
      onExited={onExited}
      // A click outside closes it, on both steps: it is what every other modal in the product does,
      // and one gesture closing while the other is swallowed reads as a dialog that is stuck. This
      // reverses an earlier decision that guarded a filled filters step against a stray click —
      // the draft it protected is reseeded from props on the next open anyway.
      // Per step: neither has a heading to name it, and «which step am I on» is the one thing a
      // name can say here that the contents cannot.
      // TODO: i18n
      label={
        location !== undefined
          ? 'Filtrar entregas de la ubicación'
          : 'Buscar ubicación por dirección'
      }
      // BOTH steps head themselves through the title slot, so the two headers stand in one place
      // rather than one being a title and the other the first row of a form. Only step 1 is RULED
      // off: its header is a search field, which reads as a control and has results below it to be
      // separated from — and that rule has to span the paper, which is why the field cannot just sit
      // at the top of the body, where a rule would stop at the body's padding. Step 2's header is a
      // caption over the form it belongs to, and the frames draw no rule under it.
      title={
        location === undefined ? (
          <CoincidencesHeader query={query} onQueryChange={setQuery} />
        ) : (
          <FiltersHeader
            location={location}
            onBack={() => {
              setLocation(undefined);
            }}
            onClose={onClose}
          />
        )
      }
      titleDivider={location === undefined}
      // The footer belongs to step 2 alone: step 1 is confirmed by picking a row, so it has no
      // action to put here. Conditional spread rather than `actions={undefined}`, which
      // `exactOptionalPropertyTypes` rejects.
      // Sentence-case, per the frames. Scoped to this footer through the façade's own prop rather
      // than the theme's `typography.button.textTransform`, which stays uppercase for the product
      // (SPEC amendment A5 — flipping it globally is ADR territory).
      {...(location !== undefined
        ? {
            actions: (
              <>
                {/* TODO: i18n */}
                <Button variant="text" sentenceCase onClick={onClose}>
                  Cancelar
                </Button>
                {/* TODO: i18n */}
                <Button variant="contained" sentenceCase onClick={handleSearch}>
                  Buscar
                </Button>
              </>
            ),
          }
        : {})}
    >
      {/* The step is DERIVED from the selected location, so no seeded step can disagree with the
          address its header shows. Mounted exclusively: each step focuses on mount. */}
      {location !== undefined ? (
        <FiltersStep
          filters={filters}
          onChange={setFilters}
          onSubmit={handleSearch}
          locationId={location.id}
          fetchLocationCarriers={fetchLocationCarriers}
          fetchDeliveryStates={fetchDeliveryStates}
        />
      ) : (
        <CoincidencesStep
          query={query}
          onPick={setLocation}
          onResolved={handleResolved}
          searchLocations={searchLocations}
        />
      )}
    </Dialog>
  );
}
